import { GroupDTO, UserDTO } from 'business/models/Models';
import React, { useCallback, useState, ChangeEvent, useMemo } from 'react';
import { IStyle } from 'Styles';
import { FormInputRow } from 'components/input';
import { FormActionButton } from 'components/Button';
import { NotificationTypes } from 'constants/index';
import { IOption } from 'components/input/selectInput';
import {
  getNewId,
  isRMWebLicensed,
  MaxNameLength,
  showAlertSnackBar,
  validateEmail,
} from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { cloneDeep } from 'lodash';
import { EUserTypes } from 'enums/EFeatureTypes';

interface Props {
  user?: UserDTO;
  closeModal: () => void;
  handleOnSave?: (userDetails: any) => void;
  tempState?: any;
  setTempState?: (...args) => any;
}

const _notificationTypes: IOption[] = NotificationTypes.map((type) => ({
  title: type.name,
  value: type.id,
}));

const getNotificationTypeById = (id: number) =>
  NotificationTypes.filter((type) => type.id == id)[0];

const editProtectedUserNames = ['Administrator', 'Default User', 'FormulatrixAdmin'];
const ALL_ROCKMAKER_USERS = 'AllRockMakerUsers';
const UserAddEdit = (props: Props) => {
  const {
    user,
    closeModal,
    handleOnSave = null,
    tempState = null,
    setTempState = null,
  } = props;

  const makeNewAllRockMakerGroup = (): GroupDTO[] => [
    GroupDTO.fromJS({
      id: tempState?.groups?.find((g) => g.name === ALL_ROCKMAKER_USERS).id,
      name: ALL_ROCKMAKER_USERS,
      isPrimaryGroup: false,
    }),
  ];

  const [_userDetail, _setUserDetail] = useState({
    id: user?.id || getNewId(),
    name: user?.name || '',
    email: user?.email || '',
    userType: user?.userType?.id || 2,
    notificationType: user?.notificationType.id || 1,
    groups: makeNewAllRockMakerGroup(),
    isGroupAdmin: false,
  });
  const [selectedFeature, setSelectedFeature] = useState<number>(_userDetail?.userType);

  const userTypeOptions: IOption[] = useMemo(() => {
    if (isRMWebLicensed()) {
      return tempState?.userTypes
        ?.filter((t) => t.id !== EUserTypes.All)
        .map((t) => ({ title: t.name, value: t.id }));
    } else {
      return tempState?.userTypes
        ?.filter((t) => t.id !== EUserTypes.All && t.id !== EUserTypes.RMLite)
        .map((t) => ({ title: t.name, value: t.id }));
    }
  }, [tempState]);

  const getUserTypeById = (id: number) =>
    tempState?.userTypes?.filter((type) => type.id == id)[0];

  const isValidEmail = validateEmail(_userDetail.email);

  const handleSetUserDetail = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      _setUserDetail((prv) => ({
        ...prv,
        [e.target.name]: isNaN(parseInt(e.target.value))
          ? e.target.value
          : parseInt(e.target.value),
      }));
    },
    [_setUserDetail],
  );

  const handleUserSave = (userData: any) => {
    const user = {
      ...userData,
      userType: getUserTypeById(userData.userType),
    };
    handleOnSave(user);
  };

  const _handleOnSave = useCallback(() => {
    let selectedUserIndex = null;
    let currentUser = null;
    let hasDuplicateEmailOrName = false;

    if (!tempState || !setTempState) {
      return;
    }

    const filteredUsers: UserDTO[] =
      tempState.users.filter((user, _ix) => {
        if (
          (_userDetail.email === user.email || _userDetail.name === user.name) &&
          _userDetail.id !== user.id
        ) {
          hasDuplicateEmailOrName = true;
        }

        if (user.id != _userDetail.id) {
          return true;
        }

        currentUser = user;
        selectedUserIndex = _ix;
        return false;
      }) || null;

    if (hasDuplicateEmailOrName) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Email or Name already in use',
      );
      return;
    }

    const addUpdateUser = !currentUser
      ? {
          ..._userDetail,
          notificationType: getNotificationTypeById(_userDetail.notificationType),
          userType: getUserTypeById(_userDetail.userType),
        }
      : {
          ...currentUser,
          name: _userDetail.name,
          email: _userDetail.email,
          notificationType: getNotificationTypeById(_userDetail.notificationType),
          userType: getUserTypeById(_userDetail.userType),
        };

    /***
     * Push New ROckamker go user group list.
     */
    const groups = cloneDeep(tempState.groups);
    if (_userDetail.id < 1) {
      groups.find((g) => g.name === ALL_ROCKMAKER_USERS)?.users?.push(addUpdateUser);
    }

    !currentUser
      ? filteredUsers.push(addUpdateUser)
      : filteredUsers.splice(selectedUserIndex, 0, addUpdateUser);

    setTempState((prv) => ({
      ...prv,
      users: filteredUsers,
      groups,
    }));

    closeModal();
  }, [closeModal, _userDetail]);

  const handleFeatureCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    const parsedValue = parseInt(value);
    const allValues = userTypeOptions
      ?.filter((o) => Number(o.value) !== EUserTypes.All)
      ?.map((o) => Number(o.value));

    if (_userDetail?.userType === EUserTypes.All && allValues.includes(parsedValue)) {
      const newValue = allValues.find((val) => val !== parsedValue);
      setSelectedFeature(newValue);
      _setUserDetail((prevState) => ({
        ...prevState,
        userType: newValue,
      }));
    } else if (allValues.includes(parsedValue)) {
      if (checked) {
        setSelectedFeature(EUserTypes.All);
        _setUserDetail((prevState) => ({
          ...prevState,
          userType: EUserTypes.All,
        }));
      }
    }
  };

  return (
    <>
      <div id="useraddedit-form-wrapper" style={styles.fields}>
        <FormInputRow
          id="useraddedit-form-input-name"
          name={'name'}
          labeltext="Name:"
          value={_userDetail.name}
          onChange={handleSetUserDetail}
          disabled={editProtectedUserNames.includes(_userDetail.name)}
          title={
            editProtectedUserNames.includes(_userDetail.name)
              ? 'You can not edit name for this user'
              : null
          }
          errorMessage={
            _userDetail.name.length > MaxNameLength.USERS
              ? `Please Enter A Name No Longer Than ${MaxNameLength.USERS} Characters`
              : ''
          }
          required
        />
        <FormInputRow
          id="useraddedit-form-input-email"
          inputType="email"
          name={'email'}
          labeltext="Email:"
          value={_userDetail.email}
          onChange={handleSetUserDetail}
          errorMessage={isValidEmail ? '' : '* Please Enter Valid Email address'}
          required
        />
        <div
          id="useraddedit-form-featureaccessibility-wrapper"
          className="flex flex-row items-center"
        >
          {!handleOnSave && (
            <>
              <div id="useraddedit-form-featureaccessibility-title" className="w-[30%]">
                Features Accessibility:
              </div>
              <div
                id="useraddedit-form-featureaccessibility-options"
                className="flex flex-row gap-4"
              >
                {userTypeOptions
                  .filter((option) => option.value !== EUserTypes.All)
                  .map((option, index) => (
                    <div
                      id={`useraddedit-form-featureaccessibility-option-${index}`}
                      key={`userType_${index}`}
                      className="flex flex-row gap-2 items-center text-base"
                    >
                      <input
                        id={option.title}
                        type="checkbox"
                        name={'userType'}
                        value={option.value}
                        checked={
                          selectedFeature === option.value ||
                          selectedFeature === EUserTypes.All
                        }
                        onChange={handleFeatureCheckboxChange}
                        disabled={!isRMWebLicensed()}
                      />
                      <label
                        id={`useraddedit-form-featureaccessibility-option-${option.title}-label`}
                        key={option.value}
                        htmlFor={option.title}
                        className="mt-2"
                      >
                        {option.title}
                      </label>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
        {!handleOnSave && (
          <FormInputRow
            id="useraddedit-form-input-notificationtype"
            options={_notificationTypes}
            inputType="select"
            name={'notificationType'}
            labeltext="NotificationType:"
            value={_userDetail.notificationType}
            onChange={handleSetUserDetail}
          />
        )}
      </div>
      <div id="useraddedit-form-actionbutton" style={styles.actionButtons}>
        <FormActionButton
          saveDisabled={
            _userDetail.name === '' || _userDetail.notificationType == 0 || !isValidEmail
          }
          saveText={!user ? 'Create' : 'Update'}
          discardText="Cancel"
          onDiscardClick={closeModal}
          onSaveClick={handleOnSave ? () => handleUserSave(_userDetail) : _handleOnSave}
        />
      </div>
    </>
  );
};

const styles: IStyle = {
  input: {
    width: '70%',
  },
  actionButtons: {
    margin: 10,
  },
  fields: {
    display: 'flex',
    gap: 10,
    flexDirection: 'column',
  },
};

export default UserAddEdit;
